import UnAuthorizedLayout from "../../../../branchAndBrandSpecific/layout/UnAuthorizedLayout";
import { FC, useEffect, useState } from "react";
import "../styles/index.scss";
import { AuthCard } from "../components/AuthCard";
import useCloudFunctionCall from "@/hooks/useCloudFunctionCall";
import {
  AuthenticationProps,
  AuthenticationResponse,
} from "../types/authenticationTypes";
import { brand } from "../../../../branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { createToast } from "@/utils/toaster/createToast";
import { useNavigate } from "react-router-dom";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import i18next from "i18next";
import {
  Flex,
  Text,
  TextField,
  Button,
  Spinner,
  Heading,
  Box,
} from "@radix-ui/themes";
import { getPasswordRules } from "../helperFunctions/getPasswordRules";
import PasswordRule from "../components/PasswordRule/PasswordRule";

export const PasswordReset: FC<{}> = () => {
  const navigate = useNavigate();
  const [userCredentials, setUserCredentials] = useState<{
    password: string;
    verifyPassword: string;
  }>({
    password: "",
    verifyPassword: "",
  });

  const [code, setCode] = useState<{
    0: number;
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  }>({
    0: null,
    1: null,
    2: null,
    3: null,
    4: null,
    5: null,
  });
  const baseAuthenticationProps = {
    method: "email",
    platform: brand.brandValue,
    tenantId: brand.tenantId,
  } as AuthenticationProps;

  //   Checks
  const isPasswordValid =
    /^(?=.*[0-9])(?=.*[!@#$%^&*()+\-\\[\]{};:'",.<>/?]).{6,}$/.test(
      userCredentials.password
    ); // Check if password contains every rule
  const passwordsMatch =
    userCredentials.password === userCredentials.verifyPassword;

  const passwordRules = getPasswordRules(userCredentials);

  const setFieldValue = async (targetName: string, value: any) => {
    if (targetName === "passwordCode") {
      if (value !== "" && !Number(value)) {
        return setUserCredentials((prev: any) => ({
          ...prev,
          [targetName]: code,
        }));
      }
      if (code && value.length > 6) {
        return setUserCredentials((prev: any) => ({
          ...prev,
          [targetName]: code,
        }));
      }

      if (Number(value)) {
        return setUserCredentials((prev: any) => ({
          ...prev,
          [targetName]: value,
        }));
      }
    }

    return setUserCredentials((prev: any) => ({
      ...prev,
      [targetName]: value,
    }));
  };

  // Auth logic
  const [response, _loading, _error, authenticate] =
    useCloudFunctionCall<AuthenticationResponse>(
      "userAuthentication",
      undefined
    );

  useEffect(() => {
    if (!localStorage.getItem("authEmailLocal")) {
      return navigate("/login");
    }
    if (code && response) {
      if (response.status === "password-reset-success") {
        createToast({
          type: "success",
          message: "Password successfully updated",
        });
        setTimeout(() => {
          return navigate(response.redirectURL);
        }, 1500);
      }
      if (response.status === "weak-password") {
        createToast({
          type: "info",
          message:
            "Password is no strong enough, please try a different password.",
        });
      }
    }
  }, [response]);

  const handleNextStep = async () => {
    let concatenatedString = "";

    Object.keys(code).forEach((key) => {
      const value = code[key];
      if (typeof value === "number") {
        concatenatedString += value.toString();
      }
    });

    await authenticate({
      ...baseAuthenticationProps,
      action: "reset-password",
      newPassword: userCredentials.password,
      emailAddress: localStorage.getItem("authEmailLocal"),
      passwordResetCode: concatenatedString,
    });
  };

  // Page Document Title
  useDocumentTitle(i18next.t("auth.reset.headline"));

  return (
    <UnAuthorizedLayout>
      <AuthCard title={i18next.t("auth.reset.headline")} type="signup">
        {/* Password code */}
        <Flex direction="column" gap="4">
          <Box>
            <Heading as="h2" size="3">
              {i18next.t("auth.reset.code")}
            </Heading>
            <Text as="p" size="2">
              {i18next.t("auth.reset.digits")}{" "}
              {localStorage.getItem("authEmailLocal")}
            </Text>
          </Box>
          <Flex gap="2">
            {Array.from(Array(6), (_, index) => {
              const key = `auth-${index}`;
              return (
                <TextField.Root
                  size="3"
                  type="text"
                  id={`authcode-${index}`}
                  maxLength={1}
                  value={code[index]}
                  key={key}
                  disabled={_loading}
                  onPaste={async (e) => {
                    const pastedCode = await navigator.clipboard.readText();

                    if (
                      pastedCode.length === 6 &&
                      Object.values(code).every((value) => value === null)
                    ) {
                      setFieldValue("passwordCode", {
                        0: Number(pastedCode[0]),
                        1: Number(pastedCode[1]),
                        2: Number(pastedCode[2]),
                        3: Number(pastedCode[3]),
                        4: Number(pastedCode[4]),
                        5: Number(pastedCode[5]),
                      });
                    }
                  }}
                  onChange={(e) => {
                    if (!e.target.value && e.target.value !== "0") {
                      return setCode((prev: any) => ({
                        ...prev,
                        [index]: null,
                      }));
                    }

                    if (
                      Number(e.target.value) ||
                      Number(e.target.value) === 0
                    ) {
                      setCode((prev: any) => ({
                        ...prev,
                        [index]: Number(e.target.value),
                      }));

                      return document
                        .getElementById(`authcode-${index}`)
                        .focus();
                    }
                  }}
                />
              );
            })}
          </Flex>

          {/* Password */}
          <Flex direction="column" gap="1">
            <Flex justify="between" align="end">
              <Text as="label" size="2">
                {i18next.t("auth.input.password.create.label")}
              </Text>
            </Flex>
            <TextField.Root
              placeholder={i18next.t("auth.input.password.create.placeholder")}
              size="3"
              type="password"
              value={userCredentials?.password}
              onChange={(e) => {
                setFieldValue("password", e.target.value);
              }}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  if (
                    passwordsMatch &&
                    userCredentials.password &&
                    userCredentials.verifyPassword
                  ) {
                    handleNextStep();
                  }
                }
              }}
            />
          </Flex>

          {/* Verify password */}
          {userCredentials.password && isPasswordValid && (
            <Flex direction="column" gap="1">
              <Flex justify="between" align="end">
                <Text as="label" size="2">
                  {i18next.t("auth.input.password.repeat.label")}
                </Text>
              </Flex>
              <TextField.Root
                placeholder={i18next.t(
                  "auth.input.password.repeat.placeholder"
                )}
                size="3"
                type="password"
                value={userCredentials?.verifyPassword}
                onChange={(e) => {
                  setFieldValue("verifyPassword", e.target.value);
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    if (
                      passwordsMatch &&
                      userCredentials.password &&
                      userCredentials.verifyPassword
                    ) {
                      handleNextStep();
                    }
                  }
                }}
              />
            </Flex>
          )}
          <Box>
            <Text as="p" mb="2">
              {i18next.t("auth.create.password.headline")}:
            </Text>
            <Flex wrap="wrap" gapY="2">
              {passwordRules.map((passRule, index) => (
                <PasswordRule
                  key={`passRule_${index}`}
                  rule={passRule.rule}
                  isValid={passRule.isValid}
                />
              ))}
            </Flex>
          </Box>

          <Button
            disabled={!isPasswordValid || !passwordsMatch}
            variant="solid"
            onClick={async (e) => {
              handleNextStep();
            }}
          >
            {_loading ? <Spinner /> : i18next.t("auth.input.button.reset")}
          </Button>
        </Flex>
      </AuthCard>
    </UnAuthorizedLayout>
  );
};
